import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import logo from "@/assets/velocity-logo-header.png";

const navItems = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Fleet", href: "/fleet" },
  { label: "Operations", href: "/operations" },
  { label: "Safety", href: "/safety" },
  { label: "Clients", href: "/client-login" },
  { label: "Employees", href: "/employee-login" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handler = () => {
      setScrolled(window.scrollY > 30);
      const footer = document.getElementById("site-footer");
      if (footer) {
        const footerTop = footer.getBoundingClientRect().top;
        const isFooterVisible = footerTop < window.innerHeight;
        if (isFooterVisible) {
          setHidden(true);
        } else {
          setHidden(false);
        }
      }
      lastScrollY = window.scrollY;
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          hidden && !mobileOpen ? "-translate-y-full" : "translate-y-0"
        } ${
          mobileOpen
            ? "h-16 bg-navy-deep"
            : scrolled
              ? "h-16 bg-navy-deep/98 backdrop-blur-2xl shadow-2xl shadow-navy-deep/50"
              : "h-20 bg-white/95 backdrop-blur-md shadow-sm"
        }`}
      >
        <div className="h-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between">
          <Link to="/" className="flex-shrink-0 relative z-[70] -ml-4 sm:-ml-8 lg:-ml-16">
            <img src={logo} alt="Velocity Offshore" className="h-20 sm:h-36 lg:h-48 transition-all duration-500" />
          </Link>

          {/* Desktop */}
          <div className="hidden lg:flex items-center gap-0">
            {navItems.map((item) => {
              const active = location.pathname === item.href;
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`relative px-5 py-2 text-[15px] font-bold tracking-wide font-body transition-colors duration-300 ${
                    active
                      ? "text-teal"
                      : scrolled
                        ? "text-white hover:text-teal-light"
                        : "text-navy-deep hover:text-teal"
                  }`}
                >
                  {item.label}
                  {active && (
                    <span className="absolute bottom-0 left-5 right-5 h-[2px] bg-gradient-to-r from-teal to-lime" />
                  )}
                </Link>
              );
            })}
          </div>

          <Link
            to="/contact"
            className="hidden lg:flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1px] uppercase rounded-full hover:shadow-lg hover:shadow-teal/25 transition-all duration-300 font-body"
          >
            Get in Touch
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>

          <button className={`lg:hidden z-[70] relative ${scrolled || mobileOpen ? "text-white" : "text-navy-deep"}`} onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile fullscreen overlay - rendered outside nav to avoid clipping */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[65] bg-navy-deep flex flex-col justify-center items-center">
          <div className="space-y-3 text-center">
            {navItems.map((item, i) => {
              const active = location.pathname === item.href;
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`block py-3 text-2xl font-display transition-colors ${
                    active ? "text-teal" : "text-white/80 hover:text-teal"
                  }`}
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  {item.label}
                </Link>
              );
            })}
            <div className="pt-8">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[13px] font-bold tracking-[1px] uppercase rounded-full font-body"
              >
                Get in Touch
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
